import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import WorkspaceContext from '../../WorkspaceContext'
import {formatNumber} from '../../../../helpers/number'
import {getType} from '../../../../apis/model/base'
import {bindQueryParams} from '../../../AppLocals/functions/routerHelper'
import {Avatar, Skeleton} from 'antd'
import {FaChalkboard, FaUserGraduate, FaUserTie} from 'react-icons/fa6'
import {ClassroomSvg} from '../../../../components/Svgs/ClassroomSvg'
import {
    course_getCourseClassesByOwner_Api,
    course_getCourseEnrollmentsByOwner_Api,
    course_getCourses_Api,
    modelAudit_getModelAudits_Api,
    timetable_getsByClassesOwner_Api,
} from 'apis'
import {BsCalendar2Event, BsClock, BsExclamationCircle, BsPeopleFill,} from 'react-icons/bs'
import {MdOutlineTimeline} from 'react-icons/md'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import {getLogin} from 'redux/auth/selectors'
import {enrollment_status_options} from 'views/AppLocals/Course'

dayjs.extend(relativeTime)

const colorMap = {
    indigo: {bg: '#ede9fe', text: '#4f46e5', icon: '#7c3aed', badge: '#dcfce7', badgeText: '#16a34a'},
    orange: {bg: '#fff7ed', text: '#c2410c', icon: '#ea580c', badge: '#ffedd5', badgeText: '#9a3412'},
    green: {bg: '#ecfdf5', text: '#047857', icon: '#059669', badge: '#d1fae5', badgeText: '#065f46'},
    blue: {bg: '#eff6ff', text: '#1d4ed8', icon: '#3b82f6', badge: '#dbeafe', badgeText: '#1e40af'},
}

const getBarColor = (pct, hasQuota) => {
    if (!hasQuota) return '#94a3b8'   // xám → không giới hạn
    if (pct >= 100) return '#ef4444' // đỏ → đầy chỉ tiêu
    return '#22c55e'                  // xanh → còn chỗ
}

const actionConfig = {
    create: {color: '#16a34a', bg: '#f0fdf4', label: 'create'},
    update: {color: '#2563eb', bg: '#eff6ff', label: 'update'},
    delete: {color: '#dc2626', bg: '#fef2f2', label: 'delete'},
    restore: {color: '#ea580c', bg: '#fff7ed', label: 'restore'},
}

const formatScheduleDate = (t, from_date, from_time) => {
    if (!from_date) return ''
    const date = dayjs(from_date)
    const timeStr = from_time ? from_time.slice(0, 5) : ''
    const suffix = timeStr ? `, ${timeStr}` : ''
    if (date.isSame(dayjs(), 'day')) return `${t('today')}${suffix}`
    return `${t(`day_short_${date.day()}`)}, ${date.format('DD/MM')}${suffix}`
}

const StatCard = ({title, value, badge, color, icon, href}) => {
    const p = colorMap[color] || colorMap.indigo
    return (
        <Link
            to={href}
            className="px-3 py-3 md:py-4 background rounded-2xl border border-color-50 hover:border-slate-300 shadow-sm hover:shadow-md flex items-center no-underline transition-all relative">
            <div className="flex flex-col gap-2">
                <p className="font-medium text-xs text-slate-400 uppercase tracking-wider leading-snug">
                    {title}
                </p>
                <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold tracking-tight leading-none text-gray-900">
                        {value}
                    </span>
                    {!!badge && (
                        <span
                            style={{padding: '4px 8px', background: p.badge, color: p.badgeText}}
                            className="font-medium text-xs rounded-full leading-none">
                            {badge}
                        </span>
                    )}
                </div>
            </div>
            <div
                style={{background: p.bg, color: p.icon, bottom: 0, right: 0, transform: 'translate(-25%, -25%)'}}
                className="w-10 h-10 flex flex-center shrink-0 shadow-inner rounded-xl absolute">
                {icon}
            </div>
        </Link>
    )
}

const QuickActionPill = ({icon, title, href, color = '#4f46e5'}) => (
    <Link
        to={href}
        className="flex items-center gap-2 background hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-sm no-underline transition-all shrink-0"
        style={{
            color: color,
            padding: '0.5rem 1rem',
            borderRadius: '0.65rem',
            fontWeight: 600,
        }}>
        <span style={{color}} className="flex">{icon}</span>
        {title}
    </Link>
)

const EnrollmentProgressBar = ({title, count, quota, courseState}) => {
    const t = useTranslate()
    const hasQuota = quota !== null && quota > 0
    const pct = hasQuota ? Math.min(100, Math.round((count / quota) * 100)) : 0
    const barColor = getBarColor(pct, hasQuota)

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-700 truncate">{title}</span>
                <span
                    className="text-xs font-bold shrink-0 tabular-nums"
                    style={{color: barColor}}>
          {hasQuota
              ? `${formatNumber(count)} / ${formatNumber(quota)} ${t('student quota')} (${pct}%)`
              : `${formatNumber(count)} ${t('students')} / ${t('unlimited')}`}
        </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: hasQuota ? `${pct}%` : '100%',
                        background: barColor,
                        opacity: 1,
                    }}
                />
            </div>
        </div>
    )
}

const AuditTableRow = ({item}) => {
    const t = useTranslate()
    if (!item) return null
    const knownCfg = actionConfig[item.action]
    const cfg = knownCfg || {color: '#9ca3af', bg: '#f9fafb'}
    const label = knownCfg ? t(cfg.label) : item.action
    const user = item.user
    const timeStr = item.last_activity_at ? dayjs(item.last_activity_at).format('HH:mm') : ''
    const initials = user?.name ? user.name.charAt(0).toUpperCase() : '?'

    return (
        <div
            className="grid items-center py-3 border-b border-slate-50 last:border-b-0 gap-3"
            style={{gridTemplateColumns: '70px 1fr 1fr'}}>
            <span className="text-base text-slate-400 tabular-nums">{timeStr}</span>
            <div className="flex items-center gap-2 min-w-0">
                {user?.avatar
                    ? <Avatar size={22} src={user.avatar}/>
                    : (
                        <Avatar
                            size={22}
                            style={{background: '#e0e7ff', color: '#4f46e5', fontWeight: 600, fontSize: 10}}>
                            {initials}
                        </Avatar>
                    )}
                <span className="text-base font-semibold text-slate-700 truncate">{user?.name ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
                {!!item.description && (
                    <span className="text-base text-slate-500 leading-snug truncate">{item.description}</span>
                )}
                <span
                    className="px-1 py-0.5 rounded text-sm font-semibold shrink-0"
                    style={{background: cfg.bg, color: cfg.color}}>
          {label}
        </span>
            </div>
        </div>
    )
}

const UpcomingSchedule = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const today = dayjs().format('YYYY-MM-DD')

    const {response, loading} = useAsync({
        apiInfo: timetable_getsByClassesOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: 1, from_time_open: today},
    })
    const items = _.chain(_.get(response, 'data.data', []))
        .filter(item => dayjs(item.from_date).isAfter(dayjs()))  // chỉ tương lai
        .groupBy(item => _.get(item, 'course_class.id'))         // group theo lớp
        .map(group => _.minBy(group, item => dayjs(item.from_date).valueOf()))  // gần nhất mỗi lớp
        .filter(Boolean)
        .sortBy(item => dayjs(item.from_date).valueOf())
        .value()
    const isToday = (date) => dayjs(date).isSame(dayjs(), 'day')

    return (
        <div className="border border-slate-200 rounded-2xl shadow-sm flex flex-col">
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50 rounded-t-2xl">
                <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                    <BsCalendar2Event size={15} className="text-indigo-500"/>
                    {t('timetables')}
                </h3>
            </div>
            <div className="flex-1 p-4 background rounded-b-2xl">
                {loading && (
                    <div className="flex flex-col gap-3">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} active paragraph={{rows: 1}} title={false}/>
                        ))}
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <div className="py-8 text-center text-sm text-slate-300 italic">
                        {t('no upcoming schedule')}
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <div className="flex flex-col gap-4">
                        {items.map((item) => {
                            const dateStr = formatScheduleDate(t, item.from_date, item.from_time)
                            const title = item.is_holiday
                                ? `${item.notes ? ` - ${item.notes}` : ''}`
                                : _.get(item, 'course_class.course.title') || _.get(item, 'course_class.title') || ''
                            const classTitle = _.get(item, 'course_class.title')
                            const todayItem = isToday(item.from_date)
                            return (
                                <div key={item.id} className="flex items-start gap-3">
                                    <div
                                        className="mt-2 w-2 h-2 rounded-full"
                                        style={{background: todayItem ? 'var(--color-secondary-600)' : '#cbd5e1'}}
                                    />
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className={`font-semibold text-sm ${todayItem ? 'text-secondary-600' : 'text-gray-700'}`}>
                                            {dateStr}
                                        </span>
                                        <span className="text-slate-700 leading-snug">{title}</span>
                                        {!!classTitle && classTitle !== title && (
                                            <span className="font-medium text-xs text-slate-500 italic leading-snug tracking-wide">
                                            {classTitle}
                                        </span>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

const AlertRow = ({href, icon, iconBg, iconColor, title, description, actionLabel, actionColor, actionBg}) => (
    <Link
        to={href}
        className="py-3 first:pt-0 last:pb-0 flex items-center gap-3 no-underline">
        <div
            style={{background: iconBg, color: iconColor}}
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
            {icon}
        </div>
        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            <span className="text-sm font-bold text-slate-800 truncate">{title}</span>
            {!!description && (
                <span className="text-xs text-slate-500 truncate">{description}</span>
            )}
        </div>
        <span
            style={{background: actionBg, color: actionColor}}
            className="px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 whitespace-nowrap">
            {actionLabel}
        </span>
    </Link>
)

const AlertsPanel = ({owner_id, owner_type}) => {
    const t = useTranslate()

    const {response: enrollResp} = useAsync({
        apiInfo: course_getCourseEnrollmentsByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: enrollment_status_options.pending.value},
    })

    const {response: classResp} = useAsync({
        apiInfo: course_getCourseClassesByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })

    const pendingCount = _.get(enrollResp, 'data.meta.pagination.total', 0)
    const allClasses = _.get(classResp, 'data.data', [])
    const noScheduleClasses = allClasses.filter((c) => !c.has_timetable)
    const visibleNoScheduleClasses = noScheduleClasses.slice(0, 3)

    if (!pendingCount && noScheduleClasses.length === 0) return null

    return (
        <div className="flex flex-col border border-orange-300 rounded-2xl shadow-md">
            <div className="px-4 md:px-6 py-3 md:py-4 flex items-center gap-2 border-b border-orange-300 bg-orange-50 rounded-t-2xl">
                <div className="w-8 h-8 rounded-lg bg-red-100 text-red-500 flex items-center justify-center shrink-0">
                    <BsExclamationCircle size={15}/>
                </div>
                <h3 className="font-bold text-slate-800 text-sm truncate">{t('needs attention')}</h3>
            </div>
            <div className="px-4 md:px-6 py-1 flex flex-col divide-y divide-slate-100 background rounded-b-2xl">
                {pendingCount > 0 && (
                    <AlertRow
                        href="/workspace/course?rel=register-for-course"
                        icon={<BsPeopleFill size={15}/>}
                        iconBg="#fee2e2"
                        iconColor="#ef4444"
                        title={`${pendingCount} ${t('pending enrollments')}`}
                        actionLabel={t('review now')}
                        actionColor="#dc2626"
                        actionBg="#fef2f2"
                    />
                )}
                {visibleNoScheduleClasses.map((c) => (
                    <AlertRow
                        key={c.id}
                        href="/workspace/course?rel=course-class-timetable"
                        icon={<BsCalendar2Event size={15}/>}
                        iconBg="#ffedd5"
                        iconColor="#ea580c"
                        title={c.title}
                        description={t('no schedule')}
                        actionLabel={t('setup now')}
                        actionColor="#c2410c"
                        actionBg="#fff7ed"
                    />
                ))}
            </div>
        </div>
    )
}

const SystemAuditLog = ({owner_id, owner_type}) => {
    const t = useTranslate()

    if (!owner_id || !owner_type) return null

    return (
        <CustomLazyPaginationWrapper
            id={`model-audits-${owner_type}-${owner_id}`}
            apiInfo={modelAudit_getModelAudits_Api}
            query={bindQueryParams([{type: owner_type}, {id: owner_id}])}
            values={{group_type: 'course'}}
            alwaysVisible={true}
            renderItem={(item) => <AuditTableRow item={item}/>}
            renderLoading={() => (
                <div className="flex flex-col gap-4 px-6 py-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} active avatar={{size: 22}} paragraph={{rows: 1}}/>
                    ))}
                </div>
            )}
            renderEmpty={() => (
                <div className="flex items-center justify-center px-6 py-8">
                    <p className="text-sm text-slate-300 italic text-center">{t('no activity')}</p>
                </div>
            )}
            renderWrap={(child) => (
                <div className="p-4 flex flex-col background rounded-b-2xl">
                    <div
                        className="grid gap-3 pb-3 border-b border-slate-100 mb-1"
                        style={{gridTemplateColumns: '70px 1fr 1fr'}}>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('time')}
                        </span>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('user')}
                        </span>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('action')}
                        </span>
                    </div>
                    {child}
                </div>
            )}
        />
    )
}

const CourseDashboard = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const login = useSelector(getLogin) || {}

    const {response: coursesResponse} = useAsync({
        apiInfo: course_getCourses_Api,
        query: bindQueryParams([{type: getType(organization)}, {id: organization?.id}]),
    })

    const allCourses = _.get(coursesResponse, 'data.data', [])
    const activeCourses = allCourses.filter((c) => {
        if (!['enrollment_open', 'in_process'].includes(c.course_state)) return false
        if (!c.time_open) return true
        const timeOpen = dayjs(c.time_open)
        return dayjs().diff(timeOpen, 'week') <= 3
    })
    const stats = organization?.workspace_settings?.courses?.statistics ?? {}

    const orgType = getType(organization)
    const orgId = organization?.id

    return (
        <div className="flex flex-col gap-6">

            {/* Header */}
            <div className="flex items-end justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('overview')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('start setting up your new training content')}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <QuickActionPill
                        icon={<BsPeopleFill size={16}/>}
                        title={t('student')}
                        href="/workspace/course?rel=student"
                        color="#059669"
                    />
                    <QuickActionPill
                        icon={<FaChalkboard size={16}/>}
                        title={t('course')}
                        href="/workspace/course?rel=course"
                        color="#4f46e5"
                    />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
                <StatCard
                    title={t('courses')}
                    value={formatNumber(stats.course_count)}
                    badge={activeCourses.length > 0 ? `${activeCourses.length} ${t('currently open')}` : undefined}
                    color="indigo"
                    icon={<FaChalkboard size={18}/>}
                    href="/workspace/course?rel=course"
                />
                <StatCard
                    title={t('classes')}
                    value={formatNumber(stats.course_class_count)}
                    color="orange"
                    icon={<ClassroomSvg size={18}/>}
                    href="/workspace/course?rel=course"
                />
                <StatCard
                    title={t('students')}
                    value={formatNumber(stats.student_count)}
                    color="green"
                    icon={<FaUserGraduate size={18}/>}
                    href="/workspace/course?rel=student"
                />
                <StatCard
                    title={t('teacher')}
                    value={formatNumber(stats.teacher_count)}
                    color="blue"
                    icon={<FaUserTie size={18}/>}
                    href="/workspace/course?rel=teacher"
                />
            </div>

            {/* Middle: progress + upcoming schedule */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* Enrollment progress */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col">
                    <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50 rounded-t-2xl">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                            <BsClock size={15} className="text-indigo-500"/>
                            {t('enrollment status')}
                        </h3>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                            {activeCourses.length} {t('course')}
                        </span>
                    </div>
                    <div className="p-4 flex-1 background rounded-b-2xl">
                        {activeCourses.length === 0 ? (
                            <div className="py-10 text-center text-sm text-slate-300 italic">
                                {t('no data')}
                            </div>
                        ) : (
                            <div className="flex flex-col divide-y divide-gray-100">
                                {activeCourses.map((course, idx) => (
                                    <div key={course.id} className="py-4 first:pt-0 last:pb-0">
                                        <EnrollmentProgressBar
                                            index={idx}
                                            title={course.title}
                                            count={course.student_count ?? 0}
                                            quota={course.student_quota ?? 0}
                                            courseState={course.course_state}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Upcoming schedule */}
                <UpcomingSchedule
                    owner_id={orgId}
                    owner_type={orgType}
                />
            </div>

            {/* Alerts */}
            <AlertsPanel
                owner_id={orgId}
                owner_type={orgType}
            />

            {/* System audit log */}
            <div className="flex flex-col background border border-color-50 rounded-2xl shadow-sm">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 rounded-t-2xl flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                        <MdOutlineTimeline size={15} className="text-indigo-500"/>
                        {t('activity log')}
                    </h3>
                </div>
                <SystemAuditLog
                    owner_id={orgId}
                    owner_type={orgType}
                />
            </div>

            <div className="h-4"/>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CourseDashboard)
